import {
    Badge,
    Button,
    Group,
    NumberInput,
    Popover,
    Stack,
    Text,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import { memo, useState, type FC } from 'react';

import type { HealthRow } from './grouping';
import { ROP_SOURCE_TOOLTIP } from './ropWindow';

type Props = {
    row: HealthRow;
    /** The unsaved choice for this row, or undefined when it has not been touched. */
    draft: number | null | undefined;
    onChange: (productId: string, windowDays: number | null) => void;
    /** False while the rate ladder is still loading, so the row cannot be edited blind. */
    ready: boolean;
    /** The window the DRR control is on, so the badge can flag a divergence. */
    viewWindow: number;
    /** Off outside edit mode — the badge then explains the number but does not change it. */
    editable: boolean;
};

/**
 * The reorder window, rendered as the badge ON the reorder point and edited in place.
 *
 * It is deliberately not a column of its own. The window is a property OF the threshold — the
 * read the number was sized on — not a peer fact sitting beside it, and giving it a column both
 * cost a grid slot and split one idea across two places.
 *
 * The editor takes a FREE NUMBER rather than a menu of presets. The windows a brand reasons in
 * are its own (45 for a seasonal line, 21 to match a supplier cycle); a fixed list either fails
 * to offer the one that is wanted or, worse, silently rounds an uploaded 45 to the nearest
 * option the first time someone opens the control.
 *
 * A GROUP ROW GETS NO CONTROL. Its members can legitimately sit on different windows — which is
 * exactly what someone grouping by tier is looking for — so a single value there would name a
 * policy that applies to some of the rows underneath and not others, and editing it would
 * silently flatten a distinction the operator was inspecting.
 */
const RopWindowCell: FC<Props> = ({
    row,
    draft,
    onChange,
    ready,
    viewWindow,
    editable,
}) => {
    const [open, setOpen] = useState(false);
    const [entry, setEntry] = useState<number | string>('');

    if (row.isGroup) {
        return (
            <Tooltip label="Members can sit on different windows — open the group to see and edit them.">
                <Text size="xs" c="dimmed">
                    —
                </Text>
            </Tooltip>
        );
    }

    const isDirty = draft !== undefined;
    // A touched row shows what it WILL be; an untouched one shows what it resolved to.
    const effective = isDirty ? draft : row.ropWindowDays;
    const shown = effective != null && effective >= 1 ? effective : row.ropWindowDays;

    const tooltip = isDirty
        ? 'Unsaved — Save reorder windows to apply'
        : `${ROP_SOURCE_TOOLTIP[row.ropWindowSource]}${
              shown !== viewWindow
                  ? ` — sized on a ${shown}d read, not the ${viewWindow}d you are viewing`
                  : ''
          }`;

    const badge = (
        <Badge
            size="xs"
            variant="light"
            // Orange marks a row worth a second look: either an unsaved edit, or a policy
            // window that differs from the one on screen — the cue that invites the edit.
            color={isDirty ? 'orange' : shown !== viewWindow ? 'orange' : 'gray'}
            // A dotted underline is the only affordance that fits inside a badge; without it
            // nothing tells the operator this particular chip is clickable.
            td={editable ? 'underline dotted' : undefined}
        >
            {shown}d{isDirty ? ' •' : ''}
        </Badge>
    );

    if (!editable) {
        return <Tooltip label={tooltip}>{badge}</Tooltip>;
    }

    const commit = (next: number | null) => {
        onChange(row.productId, next);
        setOpen(false);
    };

    return (
        <Popover
            opened={open}
            onChange={setOpen}
            position="bottom-end"
            withinPortal
            shadow="md"
            width={230}
        >
            <Popover.Target>
                <Tooltip label={tooltip} disabled={open}>
                    <UnstyledButton
                        disabled={!ready}
                        onClick={() => {
                            setEntry(shown ?? '');
                            setOpen((o) => !o);
                        }}
                        aria-label={`Reorder DRR window for ${row.productId}`}
                    >
                        {badge}
                    </UnstyledButton>
                </Tooltip>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="xs">
                    <Text size="xs" c="dimmed">
                        Reorder point is sized on this many days of demand.
                    </Text>
                    <NumberInput
                        size="xs"
                        min={1}
                        max={365}
                        value={entry}
                        onChange={setEntry}
                        // Enter commits, so the common case never needs the mouse twice.
                        onKeyDown={(event) => {
                            if (event.key !== 'Enter') return;
                            const days = Number(entry);
                            if (Number.isFinite(days) && days >= 1) commit(Math.round(days));
                        }}
                        // A grid keystroke must not reach the sheet underneath.
                        onKeyDownCapture={(event) => event.stopPropagation()}
                        aria-label="Reorder DRR window in days"
                        data-autofocus
                    />
                    <Group gap="xs" justify="space-between" wrap="nowrap">
                        {/*
                          * Clearing is not the same as typing the inherited number back in: it
                          * removes the SKU's override so the row follows its tier rule or the
                          * merchant default again, including when that default later changes.
                          */}
                        <Button
                            size="compact-xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => commit(null)}
                        >
                            Inherit
                        </Button>
                        <Button
                            size="compact-xs"
                            onClick={() => {
                                const days = Number(entry);
                                if (Number.isFinite(days) && days >= 1) {
                                    commit(Math.round(days));
                                }
                            }}
                        >
                            Apply
                        </Button>
                    </Group>
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
};

/**
 * Memoised on the props that actually change the cell. The grid re-renders on every keystroke
 * in its search box, and a popover per row is not free — without this, typing in a 2,000-row
 * grid rebuilds two thousand of them per character.
 */
export default memo(RopWindowCell);
